import React, { FC, useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Popper from '@mui/material/Popper';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Calendar, XClose } from '@im/ui-lib/zenith/icons';
import { useTranslate } from '@im/react-localization';

interface DateProps {
  effectiveDate?: string;
  expirationDate?: string;
  handleFieldChange: (field: string, value: string | string[]) => void;
}

const CustomDateRangePicker: FC<DateProps> = ({
  effectiveDate,
  expirationDate,
  handleFieldChange,
}) => {
  const translate = useTranslate();
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleInputClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  useEffect(() => {
    setStartDate(effectiveDate ? dayjs(effectiveDate) : null);
    setEndDate(expirationDate ? dayjs(expirationDate) : null);
  }, [effectiveDate, expirationDate]);

  useEffect(() => {
    if (startDate && endDate) {
      setAnchorEl(null);
    }
  }, [startDate, endDate]);

  const open = Boolean(anchorEl);
  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box alignItems="center" display="flex" flexDirection="column">
        <TextField
          InputProps={{
            endAdornment: startDate && endDate && (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={handleClear}>
                  <XClose />
                </IconButton>
              </InputAdornment>
            ),
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Calendar />
              </InputAdornment>
            ),
          }}
          fullWidth
          onClick={handleInputClick}
          placeholder={translate('Common.SelectDates')}
          value={
            startDate && endDate
              ? `${startDate.format('MM/DD/YYYY')} - ${endDate.format(
                  'MM/DD/YYYY'
                )}`
              : ''
          }
          variant="outlined"
        />
        <Popper
          anchorEl={anchorEl}
          open={open}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
        >
          <Box
            bgcolor="aliceblue"
            borderRadius={2}
            boxShadow={3}
            display="flex"
            gap={2}
            onClick={(e) => e.stopPropagation()}
            p={2}
          >
            <Box>
              <DatePicker
                label={translate('Common.Start')}
                onChange={(newDate) => {
                  setStartDate(newDate);
                  handleFieldChange('effective_date', newDate?.toJSON() ?? '');
                  if (newDate && endDate && newDate.isAfter(endDate)) {
                    setEndDate(null);
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
                value={startDate}
              />
            </Box>
            <Box>
              <DatePicker
                disablePast
                label={translate('Common.End')}
                minDate={startDate}
                onChange={(newDate) => {
                  setEndDate(newDate);
                  handleFieldChange('expiration_date', newDate?.toJSON() ?? '');
                }}
                renderInput={(params) => <TextField {...params} />}
                value={endDate}
              />
            </Box>
          </Box>
        </Popper>
      </Box>
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
